import { Box, Chip, Link } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { DataGridComponent } from "../common/DataGridComponent";
import {
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  GridSortModel,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid-pro";
import { useAppSelector, AppState, useAppDispatch } from "../../store";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useUsers } from "../../hooks/useUsers";
import {
  setIsApproved,
  setIsEditUser,
  setOpenUserModal,
  setSelectUserData,
} from "../../store/slices/usersSlice";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import { AlertDialog } from "../common/AlertDailog";
import UserModal from "./UserModal";
import { useSnackbar } from "../../hooks/useSnackbar";
import { usePermissionWrapper } from "../../hooks/usePermissionWrapper";
import { initialPaginationModelValues } from "../../constants/paginationConstants";
import { debounce } from "lodash";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { deleteReasonSchema } from "../../config/schemas/deleteReasonSchema";
import AuditDeleteReason from "../common/AuditDeleteReason";
// import { boolean } from "zod";

const UserList = () => {
  const openUsersModal = useAppSelector(
    (state: AppState) => state.usersSlice?.openUsersModal
  );
  const dispatch = useAppDispatch();
  const { checkPermission } = usePermissionWrapper();
  const {
    getAllUserFunc,
    getAllUserData,
    getAllUserSuccess,
    getAllUserIsLoading,
    getAllUserError,
    getAllUserIsError,
    selectUser,
    deleteUser,
    deleteUserIsSuccess,
  } = useUsers();
  const { showSuccess, handleError } = useSnackbar();
  const [rowsCount, setRowsCount] = useState<number>(0);
  const [userData, setUserData] = useState([]);
  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModelValues
  );
  const [deleteUserPopup, setDeleteUserPopup] = useState<boolean>(false);
  const [deleteUserEmail, setDeleteUserEmail] = useState<string>("");
  const [filterObj, setFilterObject] = useState("");
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "email", sort: "asc" },
  ]);
  const handleSortModelChange = useCallback(
    debounce((model: GridSortModel) => {
      setSortModel(model);
    }, 300),
    []
  );
  const OnEditUser = async (id: GridRowId, btn: string) => {
    const response: any = await selectUser({ email: id, is_audit: true });
    if (response?.data) {
      const userObj = response?.data.user_details;
      dispatch(
        setSelectUserData({
          first_name: userObj.first_name,
          last_name: userObj.last_name,
          email: userObj.email,
          default_role: userObj.default_role,
          roles: response.data.roles.map((ele: any) => ele.code),
        })
      );
    }
    btn === "Edit"
      ? dispatch(setIsEditUser(true))
      : dispatch(setIsApproved(true));
    dispatch(setOpenUserModal(true));
  };
  const handleDeleteUserwithReason = (details: any) => {
    const data = {
      reason: details?.delete_reason?.code_list_value,
      reason_value_code: details?.delete_reason?.code_list_value_code,
      reason_code: "EDIT_REASON",
      comments: details.delete_comment,
    };
    handleDeleteUser(data);
  };

  const handleDeleteUser = (data?: any) => {
    data
      ? deleteUser({ email: deleteUserEmail, data })
      : deleteUser({ email: deleteUserEmail });
    setDeleteUserPopup(false);
  };

  useEffect(() => {
    if (deleteUserIsSuccess) {
      getAllUserFunc({});
      reset();
      showSuccess(
        `User ${deleteUserEmail} deleted successfully`,
        "user-delete-success"
      );
    }
  }, [deleteUserIsSuccess]);

  const columns: GridColDef[] = [
    {
      field: "email",
      headerName: "Email",
      minWidth: 120,
      flex: 1,
      renderCell: (params: any) =>
        checkPermission("EDIT_USER") ? (
          <Link onClick={() => OnEditUser(params.row.id, "Edit")}>
            {params.row.email}
          </Link>
        ) : (
          params.row.email
        ),
    },
    {
      field: "first_name",
      headerName: "First Name",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "default_role",
      headerName: "Default Role",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 100,
      type: "singleSelect",
      valueOptions: [
        { value: true, label: "Yes" },
        { value: false, label: "No" },
      ],
      filterOperators: getGridSingleSelectOperators().filter(
        (operator) => operator.value === "is"
      ),
      renderCell: (params: any) =>
        params.row?.is_active ? (
          <Chip label="Yes" color="success" size="small" variant="outlined" />
        ) : (
          <Chip label="No" color="error" size="small" variant="outlined" />
        ),
    },
    {
      field: "actions",
      width: 100,
      align: "center",
      type: "actions",
      headerName: "Actions",
      sortable: false,
      getActions: (params) => [
        <GridActionsCellItem
          key={params.row.id}
          icon={<EditIcon />}
          label="Edit"
          onClick={() => OnEditUser(params.row.id, "Edit")}
          showInMenu
          disabled={!checkPermission("EDIT_USER")}
        />,
        <GridActionsCellItem
          key={params.row.id}
          disabled={params.row.is_active || !checkPermission("APPROVE_USER")}
          icon={<HowToRegIcon />}
          label="Approve"
          onClick={() => OnEditUser(params.row.id, "Approve")}
          showInMenu
        />,
        <GridActionsCellItem
          key={params.row.id}
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => {
            setDeleteUserPopup(true);
            setDeleteUserEmail(params.row.email);
          }}
          showInMenu
          disabled={!checkPermission("DELETE_USER")}
        />,
      ],
    },
  ];

  const getAllUsers = (
    from: number,
    size: number,
    filterObj?: any,
    is_audit?: boolean
  ) => {
    getAllUserFunc({
      from,
      size,
      value: filterObj && filterObj?.quickFilter ? filterObj?.quickFilter : "",
      sort_by: sortModel?.[0]?.field,
      sort_order: sortModel?.[0]?.sort,
      data: filterObj && filterObj?.advanceFilter,
      is_audit: is_audit || false,
    });
  };

  useEffect(() => {
    getAllUserFunc({ is_audit: true });
  }, []);

  useEffect(() => {
    if (paginationModel?.pageSize) {
      const { page, pageSize } = paginationModel;
      if (page * pageSize > rowsCount - 1) {
        getAllUsers(0, pageSize, filterObj);
      } else {
        getAllUsers(page * pageSize, pageSize, filterObj);
      }
    }
  }, [paginationModel, filterObj]);

  useEffect(() => {
    if (getAllUserSuccess && getAllUserData) {
      setRowsCount(getAllUserData?.count);
      const data = getAllUserData?.result?.map((ele: any) => {
        return {
          ...ele,
          id: ele?.email,
        };
      });
      setUserData(data);
    } else if (getAllUserData?.count == 0) {
      setRowsCount(0);
      setUserData([]);
    }
  }, [getAllUserData, getAllUserSuccess]);

  useEffect(() => {
    if (getAllUserIsError) {
      setUserData([]);
      setRowsCount(0);
      const error: any = getAllUserError;
      handleError(error);
    }
  }, [getAllUserIsError]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    resolver: zodResolver(deleteReasonSchema),
    defaultValues: { delete_reason: "", delete_comment: "" },
  });

  return (
    <>
      <Box sx={{ width: "100%", height: "calc(100vh - 234px)" }}>
        <DataGridComponent
          columns={columns}
          rows={userData}
          paginationMode="server"
          showQuickFilter
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={getAllUserIsLoading}
          rowsCount={rowsCount}
          filterMode="server"
          filterValues={(data: any) => setFilterObject(data)}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          exportFileName="User_List"
        />
        <AlertDialog
          open={deleteUserPopup}
          title="Delete User"
          description={`Are you sure you want to delete the user <strong>${deleteUserEmail}</strong>?`}
          handleClose={() => {
            reset();
            setDeleteUserPopup(false);
          }}
          handleConfirm={
            checkPermission("OVERRIDE_AUDIT_REASON")
              ? () => handleDeleteUser()
              : handleSubmit(handleDeleteUserwithReason)
          }
          confirmBtn="Yes"
          cancelBtn="No"
          maxWidth="xs"
        >
          {!checkPermission("OVERRIDE_AUDIT_REASON") && (
            <AuditDeleteReason
              register={register}
              errors={errors}
              control={control}
            />
          )}
        </AlertDialog>
      </Box>
      {openUsersModal && (
        <UserModal
          getAllUsers={getAllUsers}
          paginationModel={paginationModel}
        />
      )}
    </>
  );
};

export default UserList;
